import Controller from '@ember/controller';
import { action, set } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import { htmlSafe } from '@ember/template';
import { cached, tracked } from '@glimmer/tracking';
import isValidEmailAddress from 'frontend-utils/utils/is-valid-email-address';
import { ISSUE_TYPES, type IssueType } from 'lumen/models/parity-manager/issue';
import type MarketParityParityManagerMailRoute from 'lumen/routes/market/parity/parity-manager/mail';
import type AjaxService from 'lumen/services/ajax';
import { MAIL_TYPE } from 'lumen/utils/constants/pm-constant-values';
import type { ModelFrom } from 'lumen/utils/routes';

export const RECEIVERS_LIMIT = 10;
export const CC_RECEIVERS_LIMIT = 5;
export const BCC_RECEIVERS_LIMIT = 5;

export const MAX_LENGTH_FREE_TEXT = 400;

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
function parseReceivers(string: string, separator = ';') {
  if (!string.length) {
    return [];
  }

  const trimmedValues = string
    .split(separator)
    .map((value) => value.trim())
    .filter((value) => value.length > 0);

  return [...new Set(trimmedValues)];
}

export default class MarketParityParityManagerMailController extends Controller {
  // @ts-expect-error: Type 'MarketParityParityManagerMailRoute' does not satisfy the constraint 'Route<unknown>'.
  declare model: ModelFrom<MarketParityParityManagerMailRoute>;

  // @ts-expect-error: Property 'queryParams' in type 'MailController' is not assignable to the same property in base type 'Controller<unknown>'.
  queryParams = [
    {
      issueId: { scope: 'controller' },
      subscriptionId: { scope: 'controller' },
      issueType: { scope: 'controller' },
      mailType: { scope: 'controller' },
    },
  ] as const;

  @service declare ajax: AjaxService;
  @service declare alerting: Services['alerting'];
  @service declare errorTracking: Services['error-tracking'];
  @service('hotel-and-market')
  declare hotelAndMarketService: Services['hotel-and-market'];
  @service declare intl: Services['intl'];
  @service declare router: Services['router'];
  @service declare toast: Services['toast'];

  @tracked bccReceivers = '';
  @tracked ccReceivers = '';
  @tracked freeText = '';
  @tracked isSendingMail = false;
  @tracked issueId = '';
  @tracked issueType: IssueType | null = null;
  @tracked mailSubject = '';
  @tracked mailType = '';
  @tracked receivers = '';
  @tracked selectedTemplateId: number | null = null;
  @tracked subscriptionId = '';

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get bccReceiversArray() {
    return parseReceivers(this.bccReceivers);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get bccReceiversErrorMessage() {
    if (this.hasInvalidBCCReceiver) {
      return this.intl.t('settings.reports.error.invalid_email');
    }
    if (this.hasTooManyBCCReceivers) {
      return this.intl.t('parity_manager.too_many_receivers_error.count', {
        count: BCC_RECEIVERS_LIMIT,
      });
    }
    return undefined;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get ccReceiversArray() {
    return parseReceivers(this.ccReceivers);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get ccReceiversErrorMessage() {
    if (this.hasInvalidCCReceiver) {
      return this.intl.t('settings.reports.error.invalid_email');
    }
    if (this.hasTooManyCCReceivers) {
      return this.intl.t('parity_manager.too_many_receivers_error.count', {
        count: CC_RECEIVERS_LIMIT,
      });
    }
    return undefined;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get hasBCCReceiversError() {
    return this.bccReceiversErrorMessage !== undefined;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get hasCCReceiversError() {
    return this.ccReceiversErrorMessage !== undefined;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get hasFreeTextError() {
    return this.freeText.length > MAX_LENGTH_FREE_TEXT;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get hasInvalidBCCReceiver() {
    return this.bccReceiversArray.some((value) => !isValidEmailAddress(value));
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get hasInvalidCCReceiver() {
    return this.ccReceiversArray.some((value) => !isValidEmailAddress(value));
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get hasInvalidReceiver() {
    return this.receiversArray.some((value) => !isValidEmailAddress(value));
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get hasReceiversError() {
    return this.receiversErrorMessage !== undefined;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get hasTooManyBCCReceivers() {
    return this.bccReceiversArray.length > BCC_RECEIVERS_LIMIT;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get hasTooManyCCReceivers() {
    return this.ccReceiversArray.length > CC_RECEIVERS_LIMIT;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get hasTooManyReceivers() {
    return this.receiversArray.length > RECEIVERS_LIMIT;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isCopyTextFlow() {
    // Currently Copy text flow is used in case of Test Bookings + wholesaler mails. Hotel mails are always HTML templates.
    return (
      this.issueType === ISSUE_TYPES.TEST_BOOKING &&
      this.mailType === MAIL_TYPE.WHOLESALER
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isMajorOTAFlow() {
    return this.issueType === ISSUE_TYPES.MAJOR_OTA;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isModalContentSize() {
    // should be true if we show the smaller content-sized modal (because the copyable textarea is much smaller)
    return this.isCopyTextFlow;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isSendButtonDisabled() {
    const hasRequiredFields =
      this.mailSubject.length > 0 && this.receiversArray.length > 0;

    const hasAnyInvalidReceiver =
      this.hasInvalidReceiver ||
      this.hasInvalidCCReceiver ||
      this.hasInvalidBCCReceiver;

    const hasTooManyReceivers =
      this.hasTooManyReceivers ||
      this.hasTooManyCCReceivers ||
      this.hasTooManyBCCReceivers;

    return (
      this.isSendingMail ||
      hasAnyInvalidReceiver ||
      hasTooManyReceivers ||
      !hasRequiredFields
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get market() {
    return this.hotelAndMarketService.market;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get receiversArray() {
    return parseReceivers(this.receivers);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get receiversErrorMessage() {
    if (this.hasInvalidReceiver) {
      return this.intl.t('settings.reports.error.invalid_email');
    }
    if (this.hasTooManyReceivers) {
      return this.intl.t('parity_manager.too_many_receivers_error.count', {
        count: RECEIVERS_LIMIT,
      });
    }
    return undefined;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get selectedTemplate() {
    if (!this.selectedTemplateId) {
      return this.templateOptions[0];
    }
    return this.templateOptions.find(
      (template) => template.id === this.selectedTemplateId,
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get sortedTemplates() {
    if (this.model.isLoading) {
      return [];
    }
    return this.model.templates.sort((a, b) => a.name.localeCompare(b.name));
  }

  @cached
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get templateOptions() {
    return this.sortedTemplates.map((template) => ({
      id: template.id,
      label: template.name,
      content: htmlSafe(template.content),
      isFreeTextAllowed: template.with_free_text,
    }));
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get templateStringContent() {
    return this.selectedTemplate?.content.toString();
  }

  showErrorPopup(customMessage = ''): void {
    if (customMessage) {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      this.alerting.showErrorAlert({
        text: customMessage,
      });
    } else {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      this.alerting.showErrorAlert();
    }
  }

  @action
  async copyText(): Promise<void> {
    // Used in case of the wholesaler flow: retrieve the textarea so that we can copy its text
    const violationMailTextArea = document.getElementById(
      'email-textarea',
    ) as HTMLTextAreaElement;

    if (violationMailTextArea) {
      // We select the textarea and the execCommand should copy the selection to the clipboard.
      // This allows users to easily copy & paste everything in their mail client.
      // eslint-disable-next-line @typescript-eslint/await-thenable
      await violationMailTextArea.select();
      // https://developer.mozilla.org/en-US/docs/Web/API/Document/execCommand
      document.execCommand('copy');
    }
  }

  @action onTemplateChange(option: { id: number; label: string }): void {
    this.selectedTemplateId = option.id;
  }

  @action redirectOnClose(): void {
    this.selectedTemplateId = null;
    this.router.transitionTo('market.parity.parity-manager');
    this.router.refresh('market.parity.parity-manager');
  }

  @action sendMail(): void {
    // Note: sendMail is used to send HTML emails. This is allowed if you're not in the Copy Text scenario
    if (
      !this.isCopyTextFlow &&
      this.issueId &&
      this.subscriptionId &&
      this.receivers &&
      !this.isSendingMail &&
      this.mailType &&
      this.receiversArray.length
    ) {
      const requestURL = `${this.ajax.host}/apigateway/v1/app/paritymanager/send_email?market_id=${this.market?.id}&subscription_id=${this.subscriptionId}`;
      const mailData = {
        recipient_type: 1,
        notification_type: 1,
        receivers: this.receiversArray,
        cc_receivers: this.ccReceiversArray,
        bcc_receivers: this.bccReceiversArray,
        issue_id: Number(this.issueId),
        subject: this.mailSubject,
        free_text: this.freeText,
        mail_type: Number(this.mailType),
        template_id: this.selectedTemplate?.id,
      };

      set(this, 'isSendingMail', true);

      this.ajax
        .request(requestURL, {
          method: 'POST',
          dataType: 'json',
          contentType: 'application/json; charset=utf-8',
          data: JSON.stringify(mailData),
        })
        .then(() => {
          set(this, 'isSendingMail', false);

          this.send('redirectOnClose');

          this.toast.success(
            this.intl.t('parity_manager.mail_sent_successfully'),
            {
              showIcon: true,
            },
          );
        })
        .catch((error) => {
          this.errorTracking.captureException(error);

          set(this, 'isSendingMail', false);

          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          if (error.payload && error.payload.user_msg) {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access
            this.showErrorPopup(error.payload.user_msg);
          } else {
            this.showErrorPopup();
          }
        });
    }
  }

  @action updateBccReceivers(event: Event): void {
    this.bccReceivers = (event.target as HTMLInputElement).value;
  }

  @action updateCcReceivers(event: Event): void {
    this.ccReceivers = (event.target as HTMLInputElement).value;
  }

  @action updateFreeText(event: Event): void {
    this.freeText = (event.target as HTMLInputElement).value;
  }

  @action updateMailSubject(event: Event): void {
    this.mailSubject = (event.target as HTMLInputElement).value;
  }

  @action updateReceivers(event: Event): void {
    this.receivers = (event.target as HTMLInputElement).value;
  }
}
